import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgeVerificationModal = ({ onVerified }) => {
  const [dob, setDob] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!dob) {
      setError('Please enter your date of birth');
      return;
    }
    if (!accepted) {
      setError('You must accept the Terms & Conditions');
      return;
    }

    // Client-side age check
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 15) {
      setError('You must be at least 15 years old to use Aptivon');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/verify-age`,
        { date_of_birth: dob, accepted_terms: true }
      );
      toast.success('Age verified successfully!');
      onVerified();
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Max date = 15 years ago from today
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 15);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" data-testid="age-verification-modal">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#0EA5E9]" />
          </div>
          <h2 className="text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Verify Your Age
          </h2>
          <p className="text-sm text-[#6B7280] mt-2">
            You must be at least 15 years old to use Aptivon
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="age-verification-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-[#111827] mb-2">Date of Birth</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={maxDateStr}
                className="w-full pl-10 pr-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
                required
                data-testid="age-dob-input"
              />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms-accept-modal"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#0EA5E9] focus:ring-[#0EA5E9]"
              data-testid="age-terms-checkbox"
            />
            <label htmlFor="terms-accept-modal" className="text-sm text-[#6B7280]">
              I confirm I am at least 15 years old and agree to the{' '}
              <Link to="/terms" className="text-[#0EA5E9] font-bold hover:underline" target="_blank">Terms & Conditions</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#0EA5E9] font-bold hover:underline" target="_blank">Privacy Policy</Link>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
            data-testid="age-verify-button"
          >
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AgeVerificationModal;
